import { useCallback, useRef, useState } from 'react';
import type { SearchResult, User } from '@peles/shared';
import { SearchableSelect } from '../common/SearchableSelect';
import { QuickCreateUserModal } from './QuickCreateUserModal';

interface UserSearchableSelectProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** Allow creating a brand-new contact inline (default true). */
  allowCreate?: boolean;
}

/**
 * A user/contact picker: the generic server-search {@link SearchableSelect}
 * wired to an inline "create a contact on the spot" flow. When the person is
 * not found, the dropdown's create action opens {@link QuickCreateUserModal};
 * the new user is created, linked, and auto-selected. Drop this in anywhere a
 * user must be picked (contacts, event clients, …).
 */
export function UserSearchableSelect({
  value,
  onChange,
  placeholder = 'Search contacts…',
  multiple = false,
  disabled = false,
  allowCreate = true,
}: UserSearchableSelectProps) {
  const [pending, setPending] = useState<{ query: string } | null>(null);
  // Resolver for the promise handed back to SearchableSelect.onCreate — settled
  // when the quick-create modal is closed (created → option, cancelled → null).
  const resolverRef = useRef<((result: SearchResult | null) => void) | null>(null);

  const handleCreate = useCallback(
    (query: string) =>
      new Promise<SearchResult | null>((resolve) => {
        resolverRef.current = resolve;
        setPending({ query });
      }),
    [],
  );

  const settle = useCallback((result: SearchResult | null) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setPending(null);
  }, []);

  const handleCreated = useCallback(
    (user: User) => settle({ id: user.id, label: user.name, subtitle: user.email }),
    [settle],
  );

  return (
    <>
      <SearchableSelect
        entityType="user"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        multiple={multiple}
        disabled={disabled}
        onCreate={allowCreate ? handleCreate : undefined}
        createLabel="New contact"
      />
      <QuickCreateUserModal
        open={pending !== null}
        initialName={pending?.query ?? ''}
        onClose={() => settle(null)}
        onCreated={handleCreated}
      />
    </>
  );
}
